import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineRecipe,
  defineSemanticTokens,
  defineSlotRecipe,
  defineTokens,
} from '@chakra-ui/react';
import { fontDefault, fontMonospaced } from '@m/styled/fonts';

import { typographyRecipe, typographyMixins } from './typography';

/** Figma Text Field heights — 56px below md (812px), 48px at md+. */
export const fieldControlHeights = {
  /** Default (size md): responsive by viewport */
  md: { base: '56px', md: '48px' },
  /** Fixed override (size lg): always 56px, e.g. bottom sheets */
  lg: '56px',
} as const;

export const buttonRecipe = defineRecipe({
  base: {
    px: '16px',
    gap: '8px',
  },
  variants: {
    size: {
      xs: {
        ...typographyMixins.ExtraSmallBodyMedium,
        px: '8px',
        h: '28px',
      },
      sm: {
        ...typographyMixins.SmallBodyMedium,
        px: '12px',
        h: '36px',
      },
      md: {
        ...typographyMixins.MainBodyMedium,
        px: '16px',
        h: '48px',
      },
      lg: {
        ...typographyMixins.MainBodyDemiBold,
        px: '16px',
        h: '56px',
        minWidth: {
          // Desktop
          lg: '96px',
        },
      },
    },
    variant: {
      primary: {
        bg: 'fill.button.primary.enabled',
        color: 'text.white',
        _hover: {
          bg: 'fill.button.primary.hover',
        },
        _active: {
          bg: 'fill.button.primary.active',
        },
        _disabled: {
          bg: 'fill.button.primary.disabled',
          color: 'text.disabled',
        },
      },
      secondary: {
        bg: 'fill.button.secondary.enabled',
        color: 'text.primary',
        _hover: {
          bg: 'fill.button.secondary.hover',
        },
        _active: {
          bg: 'fill.button.secondary.active',
        },
        _disabled: {
          bg: 'fill.button.secondary.disabled',
          color: 'text.disabled',
        },
      },
      tertiary: {
        bg: 'fill.button.tertiary.enabled',
        borderColor: 'stroke.border.tertiary.enabled',
        color: 'text.primary',
        _hover: {
          bg: 'fill.button.tertiary.hover',
          borderColor: 'stroke.button.tertiary.hover',
        },
        _active: {
          bg: 'fill.button.tertiary.active',
          borderColor: 'stroke.button.tertiary.active',
        },
        _disabled: {
          bg: 'fill.button.tertiary.disabled',
          color: 'text.disabled',
          borderColor: 'stroke.button.tertiary.disabled',
        },
      },
      brand: {
        bg: 'fill.button.pay.enabled',
        color: 'text.white',
        _hover: {
          bg: 'fill.button.pay.hover',
        },
        _active: {
          bg: 'fill.button.pay.active',
        },
        _disabled: {
          bg: 'fill.button.pay.disabled',
          color: 'text.disabled',
        },
      },
      link: {
        ...typographyMixins.Link,
      },
    },
    destructive: {
      true: {},
    },
  },
  compoundVariants: [
    {
      destructive: true,
      variant: 'primary',
      css: {
        bg: 'fill.button.destructive-primary.enabled',
        _hover: {
          bg: 'fill.button.destructive-primary.hover',
        },
        _active: {
          bg: 'fill.button.destructive-primary.active',
        },
        _disabled: {
          bg: 'fill.button.destructive-primary.disabled',
          color: 'text.disabled',
        },
      },
    },
    {
      destructive: true,
      variant: 'secondary',
      css: {
        bg: 'fill.button.destructive-secondary.enabled',
        borderColor: 'stroke.button.destructive-secondary.enabled',
        color: 'text.destructive-secondary.enabled',
        _hover: {
          bg: 'fill.button.destructive-secondary.hover',
          borderColor: 'stroke.button.destructive-secondary.hover',
          color: 'text.destructive-secondary.hover',
        },
        _active: {
          bg: 'fill.button.destructive-secondary.active',
          borderColor: 'stroke.button.destructive-secondary.active',
          color: 'text.destructive-secondary.active',
        },
        _disabled: {
          bg: 'fill.button.destructive-secondary.disabled',
          borderColor: 'stroke.button.destructive-secondary.disabled',
          color: 'text.disabled',
        },
      },
    },
  ],
});

export const navigationButtonRecipe = defineRecipe({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '6px',
    p: 0,
    minW: 'unset',
    bg: 'fill.button.navigation.enabled',
    color: 'fill.icon.interactive',
    cursor: 'pointer',
    _hover: {
      bg: 'fill.button.navigation.hover',
    },
    _active: {
      bg: 'fill.button.navigation.active',
    },
    _disabled: {
      bg: 'fill.button.navigation.disabled',
      color: 'fill.icon.disabled',
      opacity: 1,
      cursor: 'not-allowed',
    },
    _focusVisible: {
      focusRingColor: 'stroke.border.focused',
    },
    '& svg': {
      flexShrink: 0,
    },
  },
  variants: {
    size: {
      sm: {
        w: '36px',
        h: '36px',
        fontSize: '24px',
      },
      md: {
        w: '48px',
        h: '48px',
        fontSize: '32px',
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export const badgeRecipe = defineRecipe({
  base: {
    ...typographyMixins.ExtraSmallBodyRegular,
    borderRadius: 'full',
    gap: 2,
  },
  variants: {
    variant: {
      brand: {
        bg: 'fill.badge.brand',
        color: 'text.badge.brand',
      },
      neutral: {
        bg: 'fill.badge.neutral',
        color: 'text.badge.neutral',
      },
      informational: {
        bg: 'fill.badge.informational',
        color: 'text.badge.informational',
      },
      positive: {
        bg: 'fill.badge.positive',
        color: 'text.badge.positive',
      },
      warning: {
        bg: 'fill.badge.warning',
        color: 'text.badge.warning',
      },
      negative: {
        bg: 'fill.badge.negative',
        color: 'text.badge.negative',
      },
      urgent: {
        bg: 'fill.badge.urgent',
        color: 'text.badge.urgent',
      },
    },
    size: {
      sm: {
        px: 2,
        py: 1,
      },
    },
  },
});

export const inputRecipe = defineRecipe({
  base: {
    borderColor: 'stroke.border.input',
    '--input-height': fieldControlHeights.md,
    height: 'var(--input-height)',
    minW: 'var(--input-height)',
    _focusVisible: {
      focusRingColor: 'stroke.border.focused',
    },
  },
  variants: {
    readOnly: {
      true: {
        bg: 'fill.input.read-only',
        color: 'text.read-only',
      },
    },
    disabled: {
      true: {
        borderColor: 'stroke.border.disabled',
        color: 'text.disabled',
      },
    },
    variant: {
      outline: {
        borderColor: 'stroke.border.input',
        focusRingColor: 'stroke.border.focused',
      },
    },
    size: {
      md: {
        ...typographyMixins.MainBodyRegular,
        px: '3',
        '--input-height': fieldControlHeights.md,
      },
      lg: {
        ...typographyMixins.MainBodyRegular,
        px: '4',
        '--input-height': fieldControlHeights.lg,
      },
    },
  },
});

export const textareaRecipe = defineRecipe({
  base: {
    minH: fieldControlHeights.md,
  },
  variants: {
    size: {
      md: {
        minH: fieldControlHeights.md,
      },
      lg: {
        minH: fieldControlHeights.lg,
      },
    },
  },
});

export const selectSlotRecipe = defineSlotRecipe({
  slots: ['root'],
  base: {
    root: {
      '--select-trigger-height': fieldControlHeights.md,
    },
  },
  variants: {
    size: {
      md: {
        root: {
          '--select-trigger-height': fieldControlHeights.md,
        },
      },
      lg: {
        root: {
          '--select-trigger-height': fieldControlHeights.lg,
        },
      },
    },
  },
});

export const nativeSelectSlotRecipe = defineSlotRecipe({
  slots: ['root'],
  base: {
    root: {
      '--select-field-height': fieldControlHeights.md,
    },
  },
  variants: {
    size: {
      md: {
        root: {
          '--select-field-height': fieldControlHeights.md,
        },
      },
      lg: {
        root: {
          '--select-field-height': fieldControlHeights.lg,
        },
      },
    },
  },
});

export const numberInputSlotRecipe = defineSlotRecipe({
  slots: ['input'],
  base: {
    input: {
      '--input-height': fieldControlHeights.md,
    },
  },
  variants: {
    size: {
      md: {
        input: {
          '--input-height': fieldControlHeights.md,
        },
      },
      lg: {
        input: {
          '--input-height': fieldControlHeights.lg,
        },
      },
    },
  },
});

export const linkRecipe = defineRecipe({
  variants: {
    variant: {
      underline: {
        ...typographyMixins.Link,
        textDecorationColor: 'inherit',
      },
    },
  },
  defaultVariants: {
    variant: 'underline',
  },
});

export const separatorRecipe = defineRecipe({
  base: {
    borderColor: 'stroke.divider.default',
  },
});

export const spinnerRecipe = defineRecipe({
  base: {
    color: 'fill.icon.descriptive',
    borderBottomColor: 'fill.icon.active',
    borderInlineStartColor: 'fill.icon.active',
  },
});

export const skeletonRecipe = defineRecipe({
  variants: {
    variant: {
      pulse: {
        background: 'fill.interactive.selected',
      },
    },
  },
});

export const cardRecipe = defineRecipe({
  base: {
    bg: 'fill.background.default',
    borderRadius: 'md',
  },
  variants: {
    variant: {
      elevated: {
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.08)',
        border: '0',
        borderWidth: '0',
      },
      outline: {
        border: '1px solid',
        borderColor: 'stroke.border.input',
      },
    },
  },
});

export const fieldRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'helperText', 'errorText', 'optionalIndicator'],
  base: {
    root: {
      gap: 1,
    },
    label: {
      ...typographyMixins.SmallBodyMedium,
    },
    helperText: {
      ...typographyMixins.ExtraSmallBodyRegular,
      mt: 0.5,
    },
    errorText: {
      ...typographyMixins.ExtraSmallBodyRegular,
      mt: 0.5,
    },
    optionalIndicator: {
      fontWeight: '400',
    },
  },
});

export const tagRecipe = defineSlotRecipe({
  slots: ['root', 'label'],
  base: {
    root: {
      borderRadius: 'sm',
      width: 'fit-content',
    },
  },
  variants: {
    variant: {
      subtle: {
        root: {
          bg: 'fill.background.light-gray',
          color: 'text.primary',
        },
      },
      solid: {
        root: {
          bg: 'fill.background.light-gray',
          color: 'text.primary',
        },
      },
      surface: {
        root: {
          bg: 'fill.background.light-gray',
          color: 'text.primary',
        },
      },
      success: {
        root: {
          bg: 'fill.background.success',
          color: 'text.success',
        },
      },
    },
    size: {
      xl: {
        root: {
          px: 3,
          py: 2.5,
        },
        label: {
          ...typographyMixins.SmallBodyMedium,
        },
      },
    },
    uncontained: {
      true: {
        root: {
          bg: 'transparent',
        },
      },
    },
  },
  defaultVariants: {
    variant: 'solid',
    size: 'xl',
  },
});

export const accordionSlotRecipe = defineSlotRecipe({
  slots: ['itemIndicator'],
  base: {
    itemIndicator: {
      mr: 4,
      color: 'text.primary',
    },
  },
});

export const dialogSlotRecipe = defineSlotRecipe({
  slots: [
    'backdrop',
    'body',
    'closeTrigger',
    'content',
    'description',
    'footer',
    'header',
    'positioner',
    'title',
    'trigger',
  ],
  base: {
    header: {
      borderBottom: '1px solid',
      borderColor: 'stroke.divider.default',
      p: 6,
    },
    footer: {
      borderTop: '1px solid',
      borderColor: 'stroke.divider.default',
      p: 6,
    },
    body: {
      p: 6,
    },
  },
});

export const statusSlotRecipe = defineSlotRecipe({
  slots: ['root', 'indicator'],
  base: {
    indicator: {
      borderWidth: '.25em',
      width: '1em',
      height: '1em',
    },
  },
  variants: {
    variant: {
      positive: {
        indicator: { bg: 'green.400', borderColor: 'green.100' },
      },
      negative: {
        indicator: { bg: 'red.400', borderColor: 'red.100' },
      },
      neutral: {
        indicator: { bg: 'gray.600', borderColor: 'gray.300' },
      },
      warning: {
        indicator: { bg: 'yellow.500', borderColor: 'yellow.100' },
      },
      info: {
        indicator: { bg: 'blue.400', borderColor: 'blue.100' },
      },
    },
  },
});

export const switchSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'control', 'thumb', 'indicator', 'helperText', 'errorText'],
  base: {
    root: {
      gap: 4,
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    },
    helperText: {
      ...typographyMixins.SmallBodyRegular,
      color: 'text.secondary',
    },
    errorText: {
      ...typographyMixins.ExtraSmallBodyRegular,
      alignItems: 'center',
      color: 'text.error',
      display: 'flex',
      gap: 1,
    },
  },
  variants: {
    variant: {
      solid: {
        control: {
          bg: 'gray.400',
          _checked: {
            bg: 'brand.400',
          },
        },
        thumb: {
          bg: 'fill.background.default',
          _checked: {
            bg: 'brand.100',
          },
        },
      },
    },

    size: {
      xs: {
        root: {
          '--switch-width': 'sizes.6',
          '--switch-height': 'sizes.3',
        },
      },
      sm: {
        root: {
          '--switch-width': 'sizes.7',
          '--switch-height': 'sizes.4',
        },
      },
      md: {
        root: {
          '--switch-width': 'sizes.9',
          '--switch-height': 'sizes.5',
        },
      },
      lg: {
        root: {
          '--switch-width': 'sizes.12',
          '--switch-height': 'sizes.7',
        },
      },
    },
  },
});

export const toastSlotRecipe = defineSlotRecipe({
  slots: ['root', 'title', 'description', 'indicator', 'closeTrigger', 'actionTrigger'],
  base: {
    root: {
      alignItems: 'center',
      boxShadow: 'md',
      gap: 2,
      px: 4,
      py: 2,
      '&[data-type=warning]': {
        bg: 'fill.badge.warning',
        color: 'text.primary',
      },
      '&[data-type=success]': {
        bg: 'fill.badge.positive',
        color: 'text.primary',
      },
      '&[data-type=neutral]': {
        bg: 'fill.badge.neutral',
        color: 'text.primary',
      },
      '&[data-type=error]': {
        bg: 'fill.badge.negative',
        color: 'text.primary',
      },
    },
    indicator: {
      boxSize: 6,
    },
    actionTrigger: {
      textStyle: 'sm',
      fontWeight: 'semibold',
      height: '8',
      px: '3',
      borderRadius: 'md',
      alignSelf: 'center',
      bg: 'fill.button.secondary.enabled',
      color: 'text.primary',
      borderWidth: '1px',
      borderColor: 'stroke.border.emphasized',
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background 200ms',
      _hover: {
        bg: 'fill.button.secondary.hover',
      },
      _active: {
        bg: 'fill.button.secondary.active',
      },
    },
  },
});

export const radioCardSlotRecipe = defineSlotRecipe({
  slots: [
    'root',
    'label',
    'item',
    'itemText',
    'itemDescription',
    'itemControl',
    'itemAddon',
    'itemIndicator',
    'itemContent',
    'indicator',
    'itemIcon',
  ],
  base: {
    root: {
      gap: 2,
    },
    item: {
      // Override Chakra UI's default box shadow
      boxShadow: 'none !important',
      _invalid: {
        borderColor: 'stroke.border.error',
      },
    },
    itemControl: {
      bg: 'fill.input.filled',
      borderWidth: '1px',
      borderColor: 'stroke.button.tertiary.enabled',
      borderRadius: 'sm',
      p: 4,
      gap: 4,
      flexDirection: 'row',
      alignItems: 'center',
      _hover: {
        bg: 'fill.interactive.hover',
      },
      _checked: {
        borderColor: 'stroke.border.focused',
        bg: 'fill.input.filled',
        _hover: {
          bg: 'fill.interactive.hover',
        },
      },
      _disabled: {
        bg: 'fill.button.tertiary.disabled',
        borderColor: 'stroke.button.tertiary.disabled',
        opacity: 1,
      },
      _invalid: {
        borderColor: 'stroke.border.error',
      },
    },
    itemContent: {
      gap: 1,
    },
    itemText: {
      ...typographyMixins.MainBodyDemiBold,
      color: 'text.primary',
      _disabled: {
        color: 'text.disabled',
      },
    },
    itemDescription: {
      ...typographyMixins.SmallBodyRegular,
      color: 'text.secondary',
      _disabled: {
        color: 'text.disabled',
      },
    },
    itemIcon: {
      color: 'fill.icon.descriptive',
      flexShrink: 0,
    },
  },
  variants: {
    size: {
      sm: {
        itemIcon: {
          fontSize: '20px',
        },
      },
      md: {
        itemIcon: {
          fontSize: '24px',
        },
      },
      lg: {
        itemIcon: {
          fontSize: '28px',
        },
      },
    },
    readOnly: {
      true: {
        itemControl: {
          bg: 'fill.input.read-only',
          borderColor: 'stroke.button.tertiary.hover',
          _checked: {
            bg: 'fill.input.read-only',
            borderColor: 'stroke.button.tertiary.hover',
          },
        },
      },
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

export const tooltipSlotRecipe = defineSlotRecipe({
  slots: ['content', 'arrow', 'arrowTip'],
  base: {
    content: {
      ...typographyMixins.SmallBodyMedium,
      maxWidth: '240px',
      whiteSpace: 'normal',
    },
  },
});

const SEGMENT_INDICATOR_MARGIN = 2;
export const segmentGroupSlotRecipe = defineSlotRecipe({
  slots: ['root', 'item', 'indicator', 'label', 'itemText', 'itemControl'],
  base: {
    root: {
      '--segment-indicator-bg': 'colors.fill.button.segmented-tab.active-background',
      bg: 'fill.button.segmented-tab.track-background',
      display: 'inline-flex',
      width: 'fit-content',
      maxW: 'max-content',
    },
    item: {
      cursor: 'pointer',
      flex: '0 0 auto',
      _disabled: {
        cursor: 'not-allowed',
      },
    },
    indicator: {
      shadow: 'none',
      width: `calc(var(--width) - ${SEGMENT_INDICATOR_MARGIN * 2}px)`,
      height: `calc(var(--height) - ${SEGMENT_INDICATOR_MARGIN * 2}px)`,
      margin: `${SEGMENT_INDICATOR_MARGIN}px`,
    },
  },
});

export const tabsSlotRecipe = defineSlotRecipe({
  slots: ['root', 'list', 'trigger', 'indicator', 'content'],
  base: {
    trigger: {
      cursor: 'pointer',
      _hover: {
        bg: 'fill.button.tertiary.hover',
      },
      _active: {
        bg: 'fill.button.tertiary.active',
      },
      _disabled: {
        cursor: 'not-allowed',
        opacity: 0.6,
      },
      '&[data-state=active]': {
        bg: 'fill.button.segmented-tab.active-background',
        fontWeight: 600,
      },
    },
  },
});

export const stepsSlotRecipe = defineSlotRecipe({
  slots: [
    'root',
    'list',
    'item',
    'trigger',
    'indicator',
    'separator',
    'content',
    'title',
    'description',
    'nextTrigger',
    'prevTrigger',
    'progress',
  ],
  base: {
    title: {
      color: 'inherit',
    },
    trigger: {
      color: 'text.primary',
      '&[data-incomplete]': {
        color: 'text.tertiary',
      },
      '&[data-complete]': {
        color: 'text.secondary',
      },
    },
    description: {
      color: 'text.secondary',
    },
    separator: {
      bg: 'border',
      _complete: {
        bg: 'border',
      },
    },
    list: {
      '--steps-thickness': '1px',
    },
  },
  variants: {
    variant: {
      subtle: {
        separator: {
          _complete: {
            bg: 'border',
          },
        },
        indicator: {
          _incomplete: {
            bg: 'fill.badge.neutral',
          },
          _current: {
            bg: 'fill.badge.neutral',
          },
          _complete: {
            bg: 'none',
          },
        },
      },
    },
    size: {
      sm: {
        root: {
          gap: '3',
        },
        list: {
          '--steps-size': 'sizes.4',
          '--steps-icon-size': 'sizes.4',
        },
      },
    },
  },
  defaultVariants: {
    size: 'sm',
    variant: 'subtle',
  },
});

export const stepperSlotRecipe = defineSlotRecipe({
  slots: ['root', 'step'],
  base: {
    root: {
      width: '100%',
      gap: 1,
    },
    step: {
      flexGrow: 1,
      height: 1,
      transition: 'background .2s',
    },
  },
  variants: {
    variant: {
      UserControlled: {
        step: {
          '&[data-state=completed]': {
            bg: 'brand.400',
          },
          '&[data-state=active]': {
            bg: 'brand.600',
          },
          '&[data-state=inactive]': {
            bg: 'gray.400',
          },
        },
      },
      SystemControlled: {
        root: {
          gap: '5px',
        },
        step: {
          height: 2,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          transition: 'background-color 0.2s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: -100,
            right: 0,
            bottom: 0,
            background: `linear-gradient(90deg,
                var(--chakra-colors-green-200) 0%,
                var(--chakra-colors-green-200) 50%,
                var(--chakra-colors-green-400) 100%)`,
            transform: 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 0.2s ease-out',
          },
          '&[data-state=completed]': {
            bg: 'green.200',
            '&::before': {
              transform: 'scaleX(2)',
            },
          },
          '&[data-state=active]': {
            bg: 'gray.300',
            '&::before': {
              transform: 'scaleX(1)',
            },
          },
          '&[data-state=inactive]': {
            bg: 'gray.300',
          },
        },
      },
    },
  },
});

export const tableSlotRecipe = defineSlotRecipe({
  slots: ['root', 'header', 'body', 'row', 'columnHeader', 'cell', 'footer', 'caption'],
  base: {
    cell: {
      ...typographyMixins.SmallBodyRegular,
      color: 'text.primary',
      verticalAlign: 'middle',
      borderBottomColor: 'stroke.divider.default',
    },
    columnHeader: {
      ...typographyMixins.SmallBodyDemiBold,
      color: 'text.primary',
      bg: 'fill.background.default',
      borderBottomColor: 'stroke.divider.default',
      verticalAlign: 'middle',
      '&[aria-sort="ascending"], &[aria-sort="descending"]': {
        bg: 'fill.interactive.hover-selected',
      },
      '&[data-sortable]:not([aria-sort])': {
        cursor: 'pointer',
        _hover: {
          bg: 'fill.interactive.hover',
        },
      },
      '&[data-sortable]:focus-visible': {
        outline: 'none',
        boxShadow: 'inset 0 0 0 1px var(--chakra-colors-stroke-border-focused)',
        bg: 'fill.background.default',
      },
    },
    header: {
      bg: 'fill.background.default',
    },
    row: {
      '&[data-interactive]': {
        cursor: 'pointer',
        minH: '64px',
        borderTopWidth: '1px',
        borderColor: 'stroke.divider.default',
        bg: 'fill.background.default',
        _hover: {
          bg: 'fill.background.light-gray',
        },
        '&[data-selected]': {
          bg: 'fill.background.light-gray',
        },
      },
    },
  },
  variants: {
    variant: {
      line: {
        cell: {
          borderBottomColor: 'stroke.divider.default',
        },
      },
      outline: {
        header: {
          bg: 'fill.background.default',
        },
      },
    },
    size: {
      sm: {
        columnHeader: {
          ...typographyMixins.ExtraSmallBodyDemiBold,
          px: '2',
          py: '2',
        },
        cell: {
          ...typographyMixins.ExtraSmallBodyRegular,
          px: '2',
          py: '2',
        },
      },
      md: {
        columnHeader: {
          ...typographyMixins.SmallBodyDemiBold,
          px: '3',
          py: '3',
        },
        cell: {
          ...typographyMixins.SmallBodyRegular,
          px: '3',
          py: '3',
        },
      },
      lg: {
        columnHeader: {
          ...typographyMixins.SmallBodyDemiBold,
          px: '4',
          py: '4',
        },
        cell: {
          ...typographyMixins.SmallBodyRegular,
          px: '4',
          py: '4',
        },
      },
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

const tokens = defineTokens({
  fonts: {
    body: { value: fontDefault },
    heading: { value: fontDefault },
    mono: { value: fontMonospaced },
  },
  radii: {
    sm: { value: '6px' },
  },
  colors: {
    // https://www.figma.com/design/ebpCij6CDTHYedGBKuiwhF/Design-System--%3E-Library-(WIP)?node-id=1-31&m=dev
    gray: {
      '000': { value: '#FFFFFF' },
      100: { value: '#F5F5F5' },
      200: { value: '#F0F0F0' },
      300: { value: '#DFDFDF' },
      400: { value: '#CCCCCC' },
      500: { value: '#9E9E9E' },
      600: { value: '#595959' },
      700: { value: '#373737' },
      800: { value: '#121212' },
      900: { value: '#000000' },
    },
    red: {
      '000': { value: '#FFF0EE' },
      100: { value: '#FBDEDA' },
      200: { value: '#FFC6BE' },
      300: { value: '#FF6652' },
      400: { value: '#D4311C' },
      500: { value: '#A51F0F' },
      600: { value: '#891406' },
      700: { value: '#761104' },
      800: { value: '#401207' },
    },
    green: {
      '000': { value: '#DFFFEE' },
      100: { value: '#C8F9E0' },
      200: { value: '#95E8BD' },
      300: { value: '#25BD6F' },
      400: { value: '#0F8F4D' },
      500: { value: '#0E733F' },
      600: { value: '#0D5C33' },
      700: { value: '#064F29' },
    },
    yellow: {
      '000': { value: '#FFF7CD' },
      100: { value: '#FDEFC3' },
      200: { value: '#FFEEB5' },
      300: { value: '#FFE690' },
      400: { value: '#FAD148' },
      500: { value: '#D4A713' },
      600: { value: '#8B6F10' },
      700: { value: '#534000' },
    },
    blue: {
      '000': { value: '#E6F2FF' },
      100: { value: '#D7EAFE' },
      200: { value: '#BFDEFF' },
      300: { value: '#73B6FE' },
      400: { value: '#1987FF' },
      500: { value: '#0063CF' },
      600: { value: '#004B9C' },
      700: { value: '#003063' },
    },
    brand: {
      '000': { value: '#F7F7FF' },
      100: { value: '#F1F0FF' },
      200: { value: '#E0DFFF' },
      300: { value: '#ABA8FF' },
      400: { value: '#5F59FF' },
      500: { value: '#4842DF' },
      600: { value: '#2D23A7' },
      700: { value: '#07036C' },
    },
  },
});

const semanticTokens = defineSemanticTokens({
  colors: {
    border: {
      DEFAULT: {
        value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
      },
      subtle: {
        value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
      },
      error: {
        value: { _light: '{colors.red.300}', _dark: '{colors.red.400}' },
      },
    },
    fg: {
      muted: {
        value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
      },
      error: {
        value: { _light: '{colors.red.300}', _dark: '{colors.red.400}' },
      },
    },
    icon: {
      interactive: {
        value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
      },
      descriptive: {
        value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
      },
      disabled: {
        value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
      },
      white: {
        value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
      },
      error: {
        value: { _light: '{colors.red.300}', _dark: '{colors.red.400}' },
      },
    },
    text: {
      primary: {
        value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
      },
      secondary: {
        value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
      },
      tertiary: {
        value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
      },
      'read-only': {
        value: { _light: '{colors.gray.700}', _dark: '{colors.gray.200}' },
      },
      disabled: {
        value: { _light: '{colors.gray.500}', _dark: '{colors.gray.600}' },
      },
      white: {
        value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
      },
      'fixed-white': {
        value: { _light: '{colors.gray.000}', _dark: '{colors.gray.000}' },
      },
      error: {
        value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
      },
      discount: {
        value: { _light: '{colors.green.400}', _dark: '{colors.green.200}' },
      },
      accent: {
        value: { _light: '{colors.brand.600}', _dark: '{colors.brand.400}' },
      },
      required: {
        value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
      },
      success: {
        value: { _light: '{colors.green.500}', _dark: '{colors.green.200}' },
      },
      link: {
        enabled: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        hover: {
          value: { _light: '{colors.gray.800}', _dark: '{colors.gray.100}' },
        },
        active: {
          value: { _light: '{colors.gray.700}', _dark: '{colors.gray.200}' },
        },
        focused: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        disabled: {
          value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
        },
      },
      'destructive-secondary': {
        enabled: {
          value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
        },
        hover: {
          value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
        },
        active: {
          value: { _light: '{colors.red.600}', _dark: '{colors.red.200}' },
        },
        focused: {
          value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
        },
        selected: {
          value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
        },
        disabled: {
          value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
        },
      },
      badge: {
        brand: {
          value: { _light: '{colors.brand.700}', _dark: '{colors.brand.000}' },
        },
        neutral: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        informational: {
          value: { _light: '{colors.blue.700}', _dark: '{colors.blue.000}' },
        },
        positive: {
          value: { _light: '{colors.green.700}', _dark: '{colors.green.000}' },
        },
        warning: {
          value: { _light: '{colors.yellow.700}', _dark: '{colors.yellow.000}' },
        },
        negative: {
          value: { _light: '{colors.red.700}', _dark: '{colors.red.000}' },
        },
        urgent: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.red.700}' },
        },
      },
    },
    status: {
      error: {
        value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
      },
      success: {
        value: { _light: '{colors.green.500}', _dark: '{colors.green.200}' },
      },
    },
    fill: {
      icon: {
        interactive: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        descriptive: {
          value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
        },
        disabled: {
          value: { _light: '{colors.gray.500}', _dark: '{colors.gray.600}' },
        },
        white: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        error: {
          value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
        },
        hover: {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
        active: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
        green: {
          value: { _light: '{colors.green.500}', _dark: '{colors.green.200}' },
        },
        success: {
          value: { _light: '{colors.green.500}', _dark: '{colors.green.200}' },
        },
        accent: {
          value: { _light: '{colors.brand.400}', _dark: '{colors.brand.300}' },
        },
        warning: {
          value: { _light: '{colors.yellow.500}', _dark: '{colors.yellow.200}' },
        },
        'fixed-white': {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.000}' },
        },
        'destructive-secondary': {
          enabled: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          hover: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          active: {
            value: { _light: '{colors.red.600}', _dark: '{colors.red.200}' },
          },
          focused: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          selected: {
            value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
          },
          disabled: {
            value: { _light: '{colors.red.300}', _dark: '{colors.red.500}' },
          },
        },
        badge: {
          brand: {
            value: { _light: '{colors.brand.700}', _dark: '{colors.brand.000}' },
          },
          neutral: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          informational: {
            value: { _light: '{colors.blue.700}', _dark: '{colors.blue.000}' },
          },
          positive: {
            value: { _light: '{colors.green.700}', _dark: '{colors.green.000}' },
          },
          warning: {
            value: { _light: '{colors.yellow.700}', _dark: '{colors.yellow.000}' },
          },
          negative: {
            value: { _light: '{colors.red.700}', _dark: '{colors.red.000}' },
          },
          urgent: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.red.700}' },
          },
        },
      },
      background: {
        default: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.800}' },
        },
        banner: {
          value: { _light: '{colors.brand.100}', _dark: '{colors.brand.600}' },
        },
        'light-gray': {
          value: { _light: '{colors.gray.100}', _dark: '{colors.gray.900}' },
        },
        accent: {
          value: { _light: '{colors.brand.100}', _dark: '{colors.brand.700}' },
        },
        error: {
          value: { _light: '{colors.red.000}', _dark: '{colors.red.800}' },
        },
        warning: {
          value: { _light: '{colors.yellow.000}', _dark: '{colors.yellow.700}' },
        },
        success: {
          value: { _light: '{colors.green.000}', _dark: '{colors.green.700}' },
        },
      },
      interactive: {
        enabled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        hover: {
          value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
        },
        selected: {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
        'hover-selected': {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
      },
      input: {
        enabled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.700}' },
        },
        hover: {
          value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
        },
        active: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        focused: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        'read-only': {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
        disabled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        error: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        filled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
      },
      badge: {
        brand: {
          value: { _light: '{colors.brand.100}', _dark: '{colors.brand.600}' },
        },
        neutral: {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.600}' },
        },
        informational: {
          value: { _light: '{colors.blue.100}', _dark: '{colors.blue.600}' },
        },
        positive: {
          value: { _light: '{colors.green.100}', _dark: '{colors.green.500}' },
        },
        warning: {
          value: { _light: '{colors.yellow.100}', _dark: '{colors.yellow.600}' },
        },
        negative: {
          value: { _light: '{colors.red.100}', _dark: '{colors.red.700}' },
        },
        urgent: {
          value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
        },
      },
      button: {
        primary: {
          enabled: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          hover: {
            value: { _light: '{colors.gray.700}', _dark: '{colors.gray.300}' },
          },
          active: {
            value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
          },
          focused: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          selected: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        secondary: {
          enabled: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          hover: {
            value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
          },
          active: {
            value: { _light: '{colors.gray.400}', _dark: '{colors.gray.500}' },
          },
          focused: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          selected: {
            value: { _light: '{colors.gray.700}', _dark: '{colors.gray.200}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        tertiary: {
          enabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          hover: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
          active: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          focused: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          selected: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          disabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
        },
        pay: {
          enabled: {
            value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
          },
          hover: {
            value: { _light: '{colors.brand.500}', _dark: '{colors.brand.300}' },
          },
          active: {
            value: { _light: '{colors.brand.600}', _dark: '{colors.brand.100}' },
          },
          focused: {
            value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
          },
          selected: {
            value: { _light: '{colors.brand.700}', _dark: '{colors.brand.100}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        accent: {
          enabled: {
            value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
          },
          hover: {
            value: { _light: '{colors.brand.500}', _dark: '{colors.brand.300}' },
          },
          active: {
            value: { _light: '{colors.brand.600}', _dark: '{colors.brand.100}' },
          },
          focused: {
            value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
          },
          selected: {
            value: { _light: '{colors.brand.700}', _dark: '{colors.brand.100}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        'destructive-primary': {
          enabled: {
            value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
          },
          hover: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          active: {
            value: { _light: '{colors.red.600}', _dark: '{colors.red.200}' },
          },
          focused: {
            value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
          },
          selected: {
            value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
          },
          disabled: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
        },
        'destructive-secondary': {
          enabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          hover: {
            value: { _light: '{colors.red.000}', _dark: '{colors.red.800}' },
          },
          active: {
            value: { _light: '{colors.red.000}', _dark: '{colors.red.800}' },
          },
          focused: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          selected: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          disabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
        },
        'segmented-tab': {
          'track-background': {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.900}' },
          },
          'active-background': {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.700}' },
          },
        },
        navigation: {
          enabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          hover: {
            value: { _light: '{colors.gray.100}', _dark: '{colors.gray.800}' },
          },
          active: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          focused: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
          selected: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
          disabled: {
            value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
          },
        },
      },
      'day-picker': {
        enabled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        hover: {
          value: { _light: '{colors.gray.100}', _dark: '{colors.gray.700}' },
        },
        active: {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.600}' },
        },
        focused: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        selected: {
          value: { _light: '{colors.gray.700}', _dark: '{colors.gray.200}' },
        },
        range: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
        today: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        disabled: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
      },
      signage: {
        'special-rate': {
          value: { _light: '{colors.brand.200}', _dark: '{colors.brand.700}' },
        },
        bubble: {
          value: { _light: '{colors.yellow.300}', _dark: '{colors.yellow.600}' },
        },
      },
    },
    stroke: {
      border: {
        input: {
          value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
        },
        disabled: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
        focused: {
          value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
        },
        'focused-wrapper': {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        hover: {
          value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
        },
        error: {
          value: { _light: '{colors.red.400}', _dark: '{colors.red.400}' },
        },
        container: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
      },
      button: {
        tertiary: {
          enabled: {
            value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
          },
          hover: {
            value: { _light: '{colors.gray.500}', _dark: '{colors.gray.400}' },
          },
          active: {
            value: { _light: '{colors.gray.600}', _dark: '{colors.gray.300}' },
          },
          focused: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          selected: {
            value: { _light: '{colors.gray.900}', _dark: '{colors.gray.000}' },
          },
          disabled: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
        },
        'destructive-secondary': {
          enabled: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          hover: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          active: {
            value: { _light: '{colors.red.600}', _dark: '{colors.red.200}' },
          },
          focused: {
            value: { _light: '{colors.red.500}', _dark: '{colors.red.300}' },
          },
          selected: {
            value: { _light: '{colors.red.700}', _dark: '{colors.red.100}' },
          },
          disabled: {
            value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
          },
        },
      },
      divider: {
        default: {
          value: { _light: '{colors.gray.200}', _dark: '{colors.gray.700}' },
        },
        emphasized: {
          value: { _light: '{colors.gray.400}', _dark: '{colors.gray.600}' },
        },
      },
    },
    radio: {
      border: {
        default: {
          value: { _light: '{colors.gray.300}', _dark: '{colors.gray.600}' },
        },
        selected: {
          value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
        },
      },
      background: {
        default: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.900}' },
        },
        selected: {
          value: { _light: '{colors.brand.400}', _dark: '{colors.brand.400}' },
        },
      },
      indicator: {
        selected: {
          value: { _light: '{colors.gray.000}', _dark: '{colors.gray.000}' },
        },
      },
    },
  },
});

export const breakpoints = {
  xs: '0px', // Phone Portrait
  sm: '600px', // Tablet Portrait
  md: '812px', // Tablet Landscape, Small Desktop
  lg: '1200px', // Desktop
  xl: '1440px', // Desktop Ultra-wide
};

const customConfig = defineConfig({
  theme: {
    recipes: {
      badge: badgeRecipe,
      button: buttonRecipe,
      navigationButton: navigationButtonRecipe,
      card: cardRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
      link: linkRecipe,
      separator: separatorRecipe,
      skeleton: skeletonRecipe,
      spinner: spinnerRecipe,
      text: typographyRecipe,
    },
    slotRecipes: {
      accordion: accordionSlotRecipe,
      dialog: dialogSlotRecipe,
      field: fieldRecipe,
      nativeSelect: nativeSelectSlotRecipe,
      numberInput: numberInputSlotRecipe,
      radioCard: radioCardSlotRecipe,
      select: selectSlotRecipe,
      segmentGroup: segmentGroupSlotRecipe,
      tabs: tabsSlotRecipe,
      status: statusSlotRecipe,
      steps: stepsSlotRecipe,
      stepper: stepperSlotRecipe,
      switch: switchSlotRecipe,
      table: tableSlotRecipe,
      tag: tagRecipe,
      toast: toastSlotRecipe,
      tooltip: tooltipSlotRecipe,
    },
    tokens,
    semanticTokens,
    breakpoints,
  },
  globalCss: {
    '*::placeholder': {
      color: 'text.tertiary',
    },
    '*::selection': {
      bg: '',
    },
  },
});

export const system = createSystem(defaultConfig, customConfig);
